import React, { useEffect } from "react"
import { useRouter } from "next/router"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { useQueryParams } from "@/hooks/useQueryParams"

import { useRunDetailsData } from "../hooks/useRunDetailsData"
import type {
  BucketClickData,
  DashboardWidgetConfig,
  DataSource,
} from "../widgets/types/core"
import { RunDetailsTable } from "./RunDetailsTable"

interface RunDetailsPanelProps {
  selectedPoint: BucketClickData | null
  metricId: string
  isMonitoring: boolean
  dataSource?: DataSource
  widgetConfig?: DashboardWidgetConfig
  onClose: () => void
}

export const RunDetailsPanel: React.FC<RunDetailsPanelProps> = ({
  selectedPoint,
  metricId,
  isMonitoring,
  dataSource,
  widgetConfig,
}) => {
  const router = useRouter()
  const queryParams = useQueryParams()
  const { slug } = router.query

  const { trackEvent } = useAnalytics()

  // Use the custom hook to get data
  const { processedRows, columns, isLoading, hasError, hasRequiredData } =
    useRunDetailsData({
      selectedPoint,
      metricId,
      isMonitoring,
      dataSource,
      widgetConfig,
    })

  // Track run details loaded
  useEffect(() => {
    if (!isLoading && hasRequiredData && processedRows.length > 0) {
      trackEvent(TRACKING_EVENTS.DASHBOARD.RUN_DETAILS_LOADED, {
        run_id: processedRows[0]?.run_id || "",
        run_count: processedRows.length,
      })
    }
  }, [isLoading, hasRequiredData, processedRows, trackEvent])

  // Handle simulation click - navigate to playground page
  const handleSimulationIdClick = (
    simulationOutputId: string,
    runId: string,
  ) => {
    queryParams.set("row", simulationOutputId)
    const url = queryParams.getHref(
      `/simulated/runs/${runId}/results/${simulationOutputId}`,
      slug as string,
    )
    queryParams.remove("row")
    window.open(url, "_blank")
  }

  return (
    <div className="h-full flex flex-col bg-surface-contrast rounded-lg">
      <div className="flex-1 overflow-auto p-4">
        {!selectedPoint ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-coval-text-muted">
              <div className="text-sm">
                Click on a data point in the chart to view run details
              </div>
            </div>
          </div>
        ) : hasError ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-red-500">
              <div className="text-sm font-medium">
                Failed to load run details
              </div>
              <div className="text-xs mt-1 text-coval-text-muted">
                Please try selecting a different data point
              </div>
            </div>
          </div>
        ) : !hasRequiredData ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-center text-coval-text-muted">
              <div className="text-sm">
                {isLoading ? "Loading run data..." : "No runs found"}
              </div>
            </div>
          </div>
        ) : (
          <RunDetailsTable
            onSimulationIdClick={handleSimulationIdClick}
            isLoading={false} // We already handle loading state above
            rows={processedRows}
            columns={columns}
          />
        )}
      </div>
    </div>
  )
}
